import { useEffect, useMemo, useState } from "react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeftRight, RotateCcw, Search } from "lucide-react";
import OrderListTable from "@/components/backoffice/till-to-till-share/order-list-table";
import OrderDetailsDialog from "@/components/dialog/order-history/order-details-dialog";
import { fetchSingleCbdOrder } from "@/redux/slice/cbd-slice/cbd-slice";
import OrderNotesDialog from "@/components/dialog/backoffice/till-to-till-share/order-notes-dialog";
import OrderShareDialog from "@/components/dialog/backoffice/till-to-till-share/order-share-dialog";
import Paginat from "@/components/lib/paginat";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "@/hooks/useDebounce";
import useAuth from "@/hooks/useAuth";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import {
  clearTillToTillShareErrors,
  fetchTillDeviceList,
  fetchTillToTillShareOrders,
  shareMultipleOrdersTillThunk,
} from "@/redux/slice/settings/till-to-till-share-slice";
import { toast } from "react-hot-toast";

const formatCurrency = (value) => `£${Number(value || 0).toFixed(2)}`;

const formatDateLabel = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const formatTimeLabel = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";

  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${hour}:${minute}`;
};

const normalizeOrderType = (orderType) => {
  if (!orderType) return "N/A";
  return orderType
    .replace(/_/g, " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizeStatus = (stage, preOrder) => {
  if (preOrder) return "Pre-order";

  const normalizedStage = String(stage || "").toLowerCase();
  if (normalizedStage === "on_going" || normalizedStage === "ongoing") {
    return "Ongoing";
  }
  if (normalizedStage === "completed") {
    return "Completed";
  }

  return "Ongoing";
};

function TillToTillShare() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const restaurantId =
    user?.restaurant_id ||
    Number(localStorage.getItem("restaurant_id")) ||
    null;

  const {
    tills,
    orders,
    pagination,
    tillDevicesLoading,
    ordersLoading,
    singleShareLoading,
    multipleShareLoading,
    tillDevicesError,
    ordersError,
    singleShareError,
    multipleShareError,
  } = useSelector((state) => state.settings.tillToTillShare);

  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;
  const [filters, setFilters] = useState({
    tillId: "",
    orderType: "",
    status: "",
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [checkoutOrder, setCheckoutOrder] = useState(null);
  const [noteOrder, setNoteOrder] = useState(null);
  const [shareOrders, setShareOrders] = useState([]);
  const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const debouncedQuery = useDebounce(query, 500);

  const tillMap = useMemo(() => {
    return new Map((tills || []).map((till) => [Number(till.id), till]));
  }, [tills]);

  const tableOrders = useMemo(() => {
    return (orders || []).map((order) => {
      const tillId =
        order?.till_id ||
        (Array.isArray(order?.shared_till_ids)
          ? order.shared_till_ids[0]
          : null);
      const till = tillMap.get(Number(tillId));
      const sharedTillIds = Array.isArray(order?.shared_till_ids)
        ? order.shared_till_ids
        : order?.till_id
          ? [order.till_id]
          : [];
      const orderItems = (order?.items || []).map((item) => ({
        name: item?.name || "N/A",
        size: item?.variant?.name || "",
        qty: item?.quantity || 0,
        price: item?.calculated_price || item?.base_price || 0,
      }));

      return {
        id: order?.id,
        orderId: order?.id,
        customerName:
          order?.customer_full_name ||
          order?.customer?.full_name ||
          "Not provided",
        customerPhone:
          order?.customer_number || order?.customer?.number || "Not provided",
        dateLabel: formatDateLabel(order?.created_at),
        dateKey: order?.created_at ? order.created_at.slice(0, 10) : "",
        accepted: formatTimeLabel(order?.created_at),
        dispatch: formatTimeLabel(order?.updated_at),
        orderType: normalizeOrderType(order?.order_type),
        discount: formatCurrency(
          order?.discount_total ?? order?.discount_amount,
        ),
        orderPrice: formatCurrency(order?.total_price ?? order?.order_total),
        loyalty: Array.isArray(order?.loyalty_discounts) && order.loyalty_discounts.length > 0
          ? order.loyalty_discounts.map((d) => d.entered_code).filter(Boolean).join(", ")
          : "N/A",
        till: till?.device_name || "N/A",
        tillId: till?.id || tillId || null,
        sharedTillIds,
        tillShareId: order?.id || "1245787",
        notes: order?.note || "Not provided",
        status: normalizeStatus(order?.stage, order?.pre_order),
        operator:
          order?.staff?.user?.first_name || order?.staff?.full_name || order?.operator || "N/A",
        items: orderItems,
        cookingTime: order?.cooking_time || 0,
        total: Number(order?.total_price ?? order?.order_total ?? 0).toFixed(2),
      };
    });
  }, [orders, tillMap]);

  const requestParams = useMemo(() => {
    const params = {
      per_page: perPage,
      sort_field: "created_at",
      sort_order: "desc",
      page: currentPage,
    };

    if (filters.tillId) {
      params.till_id = Number(filters.tillId);
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.orderType) {
      params.order_type = filters.orderType;
    }
    if (debouncedQuery.trim()) {
      params.search_term = debouncedQuery.trim();
    }
    if (dateFilter) {
      params.start_date = dateFilter;
      params.end_date = dateFilter;
    }

    return params;
  }, [
    currentPage,
    dateFilter,
    debouncedQuery,
    filters.orderType,
    filters.status,
    filters.tillId,
  ]);

  useEffect(() => {
    dispatch(fetchTillDeviceList());
  }, [dispatch]);

  useEffect(() => {
    if (!restaurantId) return;

    dispatch(fetchTillToTillShareOrders({ restaurantId, params: requestParams }));
  }, [dispatch, requestParams, restaurantId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedQuery,
    dateFilter,
    filters.tillId,
    filters.orderType,
    filters.status,
  ]);

  useEffect(() => {
    if (!tillDevicesError && !ordersError && !singleShareError && !multipleShareError) {
      return;
    }

    const message =
      tillDevicesError ||
      ordersError ||
      singleShareError ||
      multipleShareError;
    toast.error(message || "Something went wrong");
    dispatch(clearTillToTillShareErrors());
  }, [
    dispatch,
    tillDevicesError,
    ordersError,
    singleShareError,
    multipleShareError,
  ]);

  const activeFilterCount =
    (query.trim() ? 1 : 0) +
    (dateFilter ? 1 : 0) +
    Object.values(filters).filter(Boolean).length;

  const selectedOrders = useMemo(
    () => tableOrders.filter((order) => selectedOrderIds.includes(order.id)),
    [selectedOrderIds, tableOrders],
  );

  const hasSelectedOrders = selectedOrderIds.length > 0;
  const allVisibleSelected =
    tableOrders.length > 0 &&
    tableOrders.every((order) => selectedOrderIds.includes(order.id));

  const openCheckout = (order) => {
    const orderId = order?.id;
    if (!orderId) return;
    setCheckoutOrder(order);
    dispatch(fetchSingleCbdOrder(orderId));
    setOpenCheckoutDialog(true);
  };

  const openNotes = (order) => {
    setNoteOrder(order);
    setOpenNoteDialog(true);
  };

  const openShare = (orders) => {
    setShareOrders(orders);
    setOpenShareDialog(true);
  };

  const clearFilters = () => {
    setQuery("");
    setDateFilter("");
    setFilters({ tillId: "", orderType: "", status: "" });
  };

  const toggleOrderSelection = (orderId, checked) => {
    setSelectedOrderIds((prev) => {
      if (checked) {
        return prev.includes(orderId) ? prev : [...prev, orderId];
      }

      return prev.filter((id) => id !== orderId);
    });
  };

  const toggleVisibleSelection = (checked) => {
    setSelectedOrderIds((prev) => {
      if (checked) {
        const visibleIds = tableOrders.map((order) => order.id);
        return Array.from(new Set([...prev, ...visibleIds]));
      }

      return prev.filter((id) => !tableOrders.some((order) => order.id === id));
    });
  };

  const handleShareConfirm = async ({ orderIds, tillIds }) => {
    if (!restaurantId) return false;

    if (Array.isArray(orderIds) && orderIds.length && Array.isArray(tillIds)) {
      const result = await dispatch(
        shareMultipleOrdersTillThunk({
          restaurantId,
          orderIds,
          tillIds,
        }),
      );

      if (result.meta.requestStatus === "fulfilled") {
        toast.success(
          orderIds.length > 1
            ? "Orders shared successfully"
            : "Order shared successfully",
        );
        setOpenShareDialog(false);
        setSelectedOrderIds([]);
        return true;
      }
    }

    return false;
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Till to Till Share" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Till to Till Share",
          path: "/backoffice/till-to-till-share",
        },
      ]}
    >
      <div className="container mt-5 flex flex-col gap-4 pb-8">
        <Card className="border border-gray-800 bg-cblack3">
          <CardContent className="flex flex-col gap-4 p-4 md:p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <div className="relative w-full min-w-[240px] max-w-[270px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cwhite3" />
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search by Order ID or Name"
                    className="border-gray-700 bg-cblack4 pl-9 text-sm text-cwhite3 placeholder:text-cwhite3/60"
                  />
                </div>

                <div className="relative w-full min-w-[180px] max-w-[220px]">
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(event) => setDateFilter(event.target.value)}
                    className="border-gray-700 bg-cblack4 text-sm text-cwhite3"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {activeFilterCount > 0 && (
                  <Button
                    onClick={clearFilters}
                    variant="ghost"
                    className="text-sm font-medium text-cred hover:bg-cred/10 hover:text-cred"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset Filter
                  </Button>
                )}
                <Select
                  value={filters.tillId || "all"}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      tillId: value === "all" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger className="h-10 w-[140px] border-gray-700 bg-cblack4 text-sm text-cwhite3">
                    <SelectValue placeholder="Filter by Till" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Filter by Till</SelectItem>
                    {(tills || []).map((till) => (
                      <SelectItem key={till.id} value={String(till.id)}>
                        {till.device_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.orderType || "all"}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      orderType: value === "all" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger className="h-10 w-[170px] border-gray-700 bg-cblack4 text-sm text-cwhite3">
                    <SelectValue placeholder="Filter by Order Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Filter by Order Type</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="table">Table</SelectItem>
                    <SelectItem value="in_house">In-House</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: value === "all" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger className="h-10 w-[150px] border-gray-700 bg-cblack4 text-sm text-cwhite3">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Filter by Status</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    {/* <SelectItem value="completed">Completed</SelectItem> */}
                    <SelectItem value="pre_order">Pre-order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-sm flex justify-between items-center text-cwhite3">
              <div>
                <span className="font-medium text-cwhite">Order List</span>
                {hasSelectedOrders && (
                  <span className="ml-2 text-cyellow">
                    {selectedOrderIds.length} selected
                  </span>
                )}
              </div>

              {hasSelectedOrders && (
                <Button
                  onClick={() => openShare(selectedOrders)}
                  className=" px-5 text-sm font-semibold text-black hover:bg-cyellow/90"
                >
                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                  Till Share
                </Button>
              )}
            </div>

            {ordersLoading ? (
              <OnlyTableSekeleton header={false} row={7} />
            ) : (
              <OrderListTable
                orders={tableOrders}
                selectedOrderIds={selectedOrderIds}
                allVisibleSelected={allVisibleSelected}
                onToggleVisibleSelection={toggleVisibleSelection}
                onToggleOrderSelection={toggleOrderSelection}
                onOpenCheckout={openCheckout}
                onOpenNotes={openNotes}
                onOpenShare={openShare}
              />
            )}

            <Paginat
              className="mt-2"
              pagination={pagination}
              onPageChange={setCurrentPage}
            />
          </CardContent>
        </Card>
      </div>

      <OrderDetailsDialog
        isOpen={openCheckoutDialog}
        onClose={() => setOpenCheckoutDialog(false)}
        selectedOrder={checkoutOrder}
      />
      <OrderNotesDialog
        isOpen={openNoteDialog}
        onClose={() => setOpenNoteDialog(false)}
        order={noteOrder}
      />
      <OrderShareDialog
        isOpen={openShareDialog}
        onClose={() => setOpenShareDialog(false)}
        orders={shareOrders}
        tills={tills}
        tillsLoading={tillDevicesLoading}
        confirmLoading={singleShareLoading || multipleShareLoading}
        onConfirm={handleShareConfirm}
        title={
          shareOrders?.length === 1
            ? `${shareOrders[0].till} (ID: ${shareOrders[0].tillId})`
            : `${shareOrders?.length || 0} Orders`
        }
      />
    </Layout2>
  );
}

export default TillToTillShare;
